import { getLocalState, setPrefix, syncLocalTab } from './StateLocal'
import { initRDT, sendRDT } from './ReduxDevTool'
import { useEffect, useState } from 'react'

import { StateListener } from './StateListener'
import { StateStorer } from './StateStorer'
import equal from 'fast-deep-equal/es6/react'

const isDev = process.env.NODE_ENV != 'production'
const _globalState = new StateStorer()
export const _listener = new StateListener(_globalState)

declare global {
  interface Window {
    _globalState: any
  }
}
if (isDev && typeof window !== 'undefined') {
  window._globalState = window._globalState || _globalState
}

type UseGlobalStateOptions<T> = {
  deepEqual?: boolean
  initialValue?: T
  /**
   * When true, always write initialValue to the store even if a value already exists.
   * Use this for page-level "owner" calls to prevent the module-level singleton from
   * leaking stale values across SSR requests, which causes hydration mismatches.
   */
  forceInitial?: boolean
}
export function useGlobalState<T>(
  key: string,
  { deepEqual, initialValue, forceInitial }: UseGlobalStateOptions<T> | undefined = {
    deepEqual: false
  }
) {
  let currentValue = _globalState.get(key) as T
  if (initialValue !== undefined && (currentValue === undefined || forceInitial)) {
    _globalState.set(key, initialValue)
    currentValue = initialValue
  }
  const [state, _setState] = useState<T | undefined>(currentValue)
  const setState = (nextState: T | undefined) => {
    _setState((_state: T | undefined) => {
      // Broadcast -> State
      if (deepEqual && equal(_state, nextState)) {
        return _state // deep compare
      }
      if (_state === nextState) {
        return _state // object compare
      }
      return nextState
    })
  }
  useEffect(() => {
    _listener.add(key, setState)
    setState(currentValue)
    return () => {
      _listener.del(key, setState)
    }
  }, [])

  const setNextState = (nextState: T | ((currentState: T) => void), event?: string) => {
    putGlobalState(key, nextState, deepEqual, event)
  }
  return [state, setNextState] as const
}

export function setGlobalState(data: any, reset = false) {
  if (reset) {
    Object.entries(data ?? {}).map(([key, nextState]) => {
      _globalState.set(key, nextState)
      _listener.delAll(key)
    })
  } else {
    Object.entries(data ?? {}).map(([key, nextState]) => {
      putGlobalState(key, nextState)
    })
  }
}

export function putGlobalState(key: string, nextState: any, deepEqual = false, event?: string) {
  const currentValue = _globalState.get(key)
  nextState = typeof nextState === 'function' ? nextState?.(currentValue) : nextState
  // State -> Broadcast
  if (deepEqual && equal(currentValue, nextState)) {
    return // deep compare
  }
  if (currentValue === nextState) {
    return // object compare
  }
  if (_globalState.set(key, nextState)) {
    sendRDT(event || key, _globalState.getState())
    _listener.broadCast(key)
  }
}

/**
 * Write a value directly into the store without broadcasting to listeners.
 * Safe to call during render (no setState side-effects on other components).
 * Use this for one-shot page-mount initialization before any useGlobalState
 * subscriber reads the key.
 */
export function forceInitGlobalState<T>(key: string, value: T) {
  _globalState.set(key, value)
}

/**
 * Read the store's current raw value for a key without subscribing. Use this
 * to check whether a value was already seeded (e.g. by an earlier page mount
 * in this same client-side session) before deciding whether to overwrite it.
 */
export function getGlobalState<T>(key: string): T | undefined {
  return _globalState.get(key) as T | undefined
}

export function clearGlobalState(key: string) {
  if (key) {
    _globalState.del(key)
    _listener.delAll(key)
  } else {
    Object.keys(_globalState).map((key) => clearGlobalState(key))
  }
}

export function initializeGlobalState() {
  const INIT_STATE =
    typeof window !== 'undefined'
      ? { ...setPrefix(window?.__NEXT_DATA__?.props?.pageProps, ''), ...getLocalState() }
      : setPrefix(process._repository, '_selless_')
  setGlobalState(INIT_STATE, true)
  initRDT(INIT_STATE)
  // Set to false to avoid clearing state, this should be changed once we apply sync between tabs of browser.
  syncLocalTab((data: any) => setGlobalState(data))
}
